import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import { STAFF_STATUS } from "../../../Shared/staffStatus";
import { StaffStatus } from "../staffStyles";

/**
 * Active / Blocked / Removed, as tabs.
 *
 * Why tabs and not the Status dropdown this replaces:
 *
 * - The three states are not peers you occasionally filter on, they are three
 *   different jobs. Active is the roster. Blocked is a queue of decisions
 *   waiting to be reversed or made permanent. Removed is an archive you consult
 *   and do not act on. A dropdown buried among Designation and Region gave all
 *   three the same weight and hid the two that need attention.
 * - The counts are the point. "3 blocked" is information you want without
 *   opening anything, and a select can't show it.
 * - It sets a default. The dropdown opened on "All statuses", so the first
 *   thing anyone saw was a list mixing people who work here with people who
 *   left. Active is what Manage Staff is for; the rest is one click away.
 *
 * Built on MUI Tabs rather than hand-rolled buttons for the keyboard behaviour:
 * arrow keys move between tabs, Tab key leaves the group, and the
 * role/aria-selected/aria-controls wiring is correct without maintaining it.
 *
 * Colour never carries the meaning on its own — every tab is labelled, and the
 * count sits next to the word.
 */

const TAB_ORDER = [
  {
    value: STAFF_STATUS.ACTIVE,
    label: "Active",
    hint: "Working here now",
  },
  {
    value: STAFF_STATUS.BLOCKED,
    label: "Blocked",
    hint: "Suspended, reversible",
  },
  {
    value: STAFF_STATUS.REMOVED,
    label: "Removed",
    hint: "Left, work handed over",
  },
];

export const StaffStatusTabs = ({ value, counts, onChange }) => {
  const accent = StaffStatus[value]?.color || colors.brown;

  return (
    <TabsBar>
      <Tabs
        value={value}
        onChange={(_event, next) => onChange(next)}
        aria-label="Staff status"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ sx: { height: 3, backgroundColor: accent, borderRadius: "3px 3px 0 0" } }}
        sx={{
          minHeight: 0,
          "& .MuiTabs-flexContainer": { gap: "2px" },
          "& .MuiTab-root": {
            // 44px keeps this usable on a touch screen; the label and the count
            // are inside one hit area rather than the count being decoration
            // beside a smaller target.
            minHeight: 46,
            minWidth: 0,
            padding: "0 14px",
            textTransform: "none",
            fontSize: "0.85rem",
            fontWeight: 600,
            color: colors.textMed,
            transition: "color 160ms ease, background 160ms ease",
            borderRadius: "8px 8px 0 0",
          },
          "& .MuiTab-root:hover": { backgroundColor: colors.lightWhite },
          "& .MuiTab-root.Mui-selected": { color: accent, fontWeight: 700 },
          "& .MuiTab-root.Mui-focusVisible": {
            outline: "2px solid #2D7AF4",
            outlineOffset: "-2px",
          },
        }}
      >
        {TAB_ORDER.map((tab) => {
          const selected = tab.value === value;
          const meta = StaffStatus[tab.value];
          return (
            <Tab
              key={tab.value}
              value={tab.value}
              disableRipple
              label={
                <TabLabel>
                  <span className="word">{tab.label}</span>
                  <Count
                    $selected={selected}
                    $color={meta.color}
                    $bg={meta.bg}
                    aria-hidden="true"
                  >
                    {counts?.[tab.value] ?? 0}
                  </Count>
                </TabLabel>
              }
              // The count is decorative markup for sighted users; screen
              // readers get it spelled out here instead of reading a bare
              // number after the word.
              aria-label={`${tab.label}, ${counts?.[tab.value] ?? 0} ${
                counts?.[tab.value] === 1 ? "employee" : "employees"
              }`}
            />
          );
        })}
      </Tabs>

      <Hint>{TAB_ORDER.find((t) => t.value === value)?.hint}</Hint>
    </TabsBar>
  );
};

const TabsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem 1rem;
  padding: 0 10px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;

  .word {
    white-space: nowrap;
  }
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 19px;
  padding: 0 6px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: ${(props) => (props.$selected ? props.$color : colors.textMed)};
  background: ${(props) => (props.$selected ? props.$bg : colors.lightWhite)};
  border: 1px solid
    ${(props) => (props.$selected ? "transparent" : colors.inputBorderWhite)};
  transition: color 160ms ease, background 160ms ease;
`;

const Hint = styled.p`
  margin: 0;
  padding-right: 8px;
  font-size: 0.75rem;
  color: ${colors.textLight};

  @media (max-width: 640px) {
    /* On a phone the tabs already scroll; a second line of explanation under
       them costs a row of table for something the label already implies. */
    display: none;
  }
`;
